import React from "react";
import { useTranslation } from "react-i18next";
import { useRetrieveOccupancy } from "@planzone/view-models";
import { CapacityGranularity, CapacitySort, Filter, Id } from "@planzone/dto";

import { WorkspaceContext } from "@scenes/workspaceContext";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Toolbar } from "./toolbar";
import { Table } from "./table";
import { Props } from "./types";
import styles from "./styles.scss";

const PAGE_SIZE = 20;

function OccupancyPage(_: Props) {
  const { t } = useTranslation();
  const [granularity, setGranularityState] =
    React.useState<CapacityGranularity>("MONTH");
  const [year, setYearState] = React.useState(new Date().getFullYear());
  const [sort, setSortState] = React.useState<CapacitySort>("TYPE");
  const [filter, setFilterState] = React.useState<Filter>({});
  const [expandedResources, setExpandedResources] = React.useState<Id[]>([]);
  const from = `${year}-01-01`;
  const to = `${year}-12-31`;
  const requestFilter = Object.keys(filter).length > 0 ? filter : undefined;
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    content,
    currentPage,
    canLoadMore,
    retrieveOccupancy,
  } = useRetrieveOccupancy(
    from,
    to,
    granularity,
    requestFilter,
    sort,
    PAGE_SIZE,
  );

  const setGranularity = (nextGranularity: CapacityGranularity) => {
    setGranularityState(nextGranularity);
    setExpandedResources([]);
  };

  const setYear = (nextYear: number) => {
    setYearState(nextYear);
    setExpandedResources([]);
  };

  const setSort = (nextSort: CapacitySort) => {
    setSortState(nextSort);
    setExpandedResources([]);
  };

  const setFilter = (nextFilter: Filter) => {
    setFilterState(nextFilter);
    setExpandedResources([]);
  };

  const toggleResource = (uuid: Id) =>
    setExpandedResources((current) =>
      current.includes(uuid)
        ? current.filter((id) => id !== uuid)
        : [...current, uuid],
    );

  const expandAll = () =>
    setExpandedResources(content.map((resource) => resource.uuid));

  const collapseAll = () => setExpandedResources([]);

  const retrieveCurrentPage = () =>
    retrieveOccupancy({
      from,
      to,
      granularity,
      filter: requestFilter,
      sort,
      page: currentPage,
      size: PAGE_SIZE,
    });

  return (
    <WorkspaceContext type="planzone">
      <div className={styles.occupancyPage}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("occupancyPage.title")}</h1>
          <div className={styles.profileNavigation}>
            <AssistantShortcut />
            <Notifications />
            <ProfileNavigation />
          </div>
        </div>
        <div className={styles.menu}>
          <Toolbar
            granularity={granularity}
            year={year}
            sort={sort}
            filter={filter}
            setGranularity={setGranularity}
            setYear={setYear}
            setSort={setSort}
            setFilter={setFilter}
          />
        </div>
        <div className={styles.content}>
          {isRequestFailure && content.length === 0 ? (
            <div className={styles.error}>
              <p>{t("occupancyPage.error")}</p>
              <p className={styles.retry} onClick={retrieveCurrentPage}>
                {t("occupancyPage.retry")}
              </p>
            </div>
          ) : isRequestSuccess && content.length === 0 ? (
            <div className={styles.empty}>
              <p>{t("occupancyPage.empty")}</p>
            </div>
          ) : (
            <InfiniteScroll
              id="occupancy"
              className={styles.tableContainer}
              canFetchOnIdle
            >
              {() => (
                <>
                  <Table
                    from={from}
                    to={to}
                    granularity={granularity}
                    sort={sort}
                    resources={content}
                    isLoadingMore={isRequestPending}
                    filter={requestFilter}
                    expandedResources={expandedResources}
                    toggleResource={toggleResource}
                    expandAll={expandAll}
                    collapseAll={collapseAll}
                  />
                  <Flag
                    name="occupancyFlag"
                    isDisabled={isRequestPending || !canLoadMore}
                    retrieveItems={retrieveCurrentPage}
                  />
                </>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </WorkspaceContext>
  );
}

export { OccupancyPage };
